/**
 * Tenant Admin — Customer Wallets page.
 *
 * Two tabs in one place:
 *   1. Withdrawals — customer-initiated UPI payouts; admin processes
 *      (Pay) or rejects (auto-refund to wallet).
 *   2. Balances — every customer's current wallet ₹ balance with a
 *      manual CREDIT/DEBIT adjustment button (writes an
 *      ``ADMIN_ADJUSTMENT`` row to the audit-trail txn ledger and
 *      optionally pushes a notification to the customer).
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, XCircle, IndianRupee, Search, Plus, Minus, Wallet as WalletIcon } from "lucide-react";
import { toast } from "sonner";
import { api, inr, inr2 } from "../../lib/api";

const STATUSES = [
    { value: "PENDING", label: "Pending", color: "bg-amber-100 text-amber-700" },
    { value: "PAID", label: "Paid", color: "bg-emerald-100 text-emerald-700" },
    { value: "REJECTED", label: "Rejected", color: "bg-red-100 text-red-700" },
];

export default function AdminWalletWithdrawals() {
    const [tab, setTab] = useState("withdrawals");
    const [filter, setFilter] = useState("PENDING");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [action, setAction] = useState(null);  // { wd, mode: 'pay'|'reject' }
    const [actionForm, setActionForm] = useState({ txn_ref: "", reason: "" });
    const [submitting, setSubmitting] = useState(false);
    // Adjust-wallet modal state — separate from withdrawal action.
    const [adjust, setAdjust] = useState(null);    // { cust }
    const [adjustForm, setAdjustForm] = useState({ direction: "CREDIT", amount: "", reason: "", notify_customer: true });
    const [adjusting, setAdjusting] = useState(false);

    const reloadWithdrawals = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/wallet-withdrawals?status=${filter}`);
            setRows(data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
        finally { setBusy(false); }
    };

    const reloadCustomers = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/customers");
            setCustomers(data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
        finally { setBusy(false); }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            if (tab === "withdrawals") await reloadWithdrawals();
            else await reloadCustomers();
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, filter]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            (r.user_name || "").toLowerCase().includes(q) ||
            (r.user_mobile || "").includes(q) ||
            (r.upi_id || "").toLowerCase().includes(q) ||
            (r.txn_ref || "").toLowerCase().includes(q),
        );
    }, [rows, search]);

    const filteredCustomers = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return customers;
        return customers.filter((c) =>
            (`${c.first_name || ""} ${c.last_name || ""}`).toLowerCase().includes(q) ||
            (c.mobile_number || "").includes(q),
        );
    }, [customers, search]);

    const submitAction = async () => {
        if (!action) return;
        setSubmitting(true);
        try {
            if (action.mode === "pay") {
                if (!actionForm.txn_ref.trim()) { toast.error("Enter UPI/UTR reference"); setSubmitting(false); return; }
                await api.post(`/admin/wallet-withdrawals/${action.wd.id}/pay`, { txn_ref: actionForm.txn_ref.trim() });
                toast.success("Marked as paid");
            } else {
                if (!actionForm.reason.trim() || actionForm.reason.trim().length < 3) { toast.error("Enter a clear rejection reason"); setSubmitting(false); return; }
                await api.post(`/admin/wallet-withdrawals/${action.wd.id}/reject`, { reason: actionForm.reason.trim() });
                toast.success("Rejected and refunded to wallet");
            }
            setAction(null); setActionForm({ txn_ref: "", reason: "" });
            reloadWithdrawals();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSubmitting(false); }
    };

    const submitAdjust = async () => {
        if (!adjust) return;
        const amt = Number(adjustForm.amount || 0);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        if (!adjustForm.reason.trim() || adjustForm.reason.trim().length < 3) { toast.error("Reason is required"); return; }
        setAdjusting(true);
        try {
            const { data } = await api.post(`/admin/customers/${adjust.cust.id}/wallet/adjust`, {
                direction: adjustForm.direction,
                amount: amt,
                reason: adjustForm.reason.trim(),
                notify_customer: adjustForm.notify_customer,
            });
            toast.success(`Wallet ${adjustForm.direction === "CREDIT" ? "credited" : "debited"} — new balance ${inr(data.new_balance)}`);
            setAdjust(null);
            setAdjustForm({ direction: "CREDIT", amount: "", reason: "", notify_customer: true });
            reloadCustomers();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setAdjusting(false); }
    };

    const totalPending = rows.filter((r) => r.status === "PENDING").reduce((s, r) => s + (r.amount || 0), 0);

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-2xl font-bold">Customer Wallets</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Process UPI withdrawals + view/adjust every customer&apos;s wallet balance.</p>
                </div>
                {tab === "withdrawals" && filter === "PENDING" && rows.length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-xl px-4 py-2 text-xs" data-testid="wd-pending-kpi">
                        <span className="font-bold text-amber-700 dark:text-amber-300">{rows.length}</span> pending · <span className="font-bold">{inr2(totalPending)}</span>
                    </div>
                )}
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 p-0.5 bg-muted rounded-lg w-fit mb-3" data-testid="wallets-tabs">
                <button
                    onClick={() => setTab("withdrawals")}
                    data-testid="wallets-tab-withdrawals"
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md ${tab === "withdrawals" ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}
                >Withdrawals</button>
                <button
                    onClick={() => setTab("balances")}
                    data-testid="wallets-tab-balances"
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md ${tab === "balances" ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}
                >Balances</button>
            </div>

            {/* Filter row */}
            {tab === "withdrawals" ? (
                <div className="bg-card rounded-2xl border border-border p-2 mb-3 flex items-center gap-2">
                    <div className="flex gap-1 p-0.5 bg-muted rounded-lg">
                        {STATUSES.map((s) => (
                            <button
                                key={s.value}
                                onClick={() => setFilter(s.value)}
                                data-testid={`wd-filter-${s.value}`}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-md ${filter === s.value ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}
                            >{s.label}</button>
                        ))}
                    </div>
                    <div className="flex-1 relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name / mobile / UPI / UTR"
                            className="w-full bg-muted rounded-lg pl-8 pr-3 py-2 text-xs outline-none"
                            data-testid="wd-search"
                        />
                    </div>
                </div>
            ) : (
                <div className="bg-card rounded-2xl border border-border p-2 mb-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search customer name or mobile"
                            className="w-full bg-muted rounded-lg pl-8 pr-3 py-2 text-xs outline-none"
                            data-testid="bal-search"
                        />
                    </div>
                </div>
            )}

            {/* Body: either withdrawals table or balances table */}
            {tab === "balances" ? (
                <div className="bg-card rounded-2xl border border-border overflow-x-auto" data-testid="bal-table">
                    {busy ? (
                        <div className="py-16 text-center"><Loader2 className="animate-spin inline" /></div>
                    ) : filteredCustomers.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground text-sm">No customers.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-muted/60 text-left">
                                <tr>
                                    <Th>Customer</Th><Th>Mobile</Th><Th>Wallet</Th><Th>Coins</Th><Th>LTV</Th><Th>Orders</Th><Th className="text-right">Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCustomers.map((c) => (
                                    <tr key={c.id} className="border-t border-border" data-testid={`bal-row-${c.id}`}>
                                        <Td>
                                            <p className="font-semibold">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}</p>
                                        </Td>
                                        <Td><span className="font-mono text-[11px]">{c.mobile_number}</span></Td>
                                        <Td className="font-bold text-emerald-700" data-testid={`bal-cell-wallet-${c.id}`}>{inr(c.wallet_balance || 0)}</Td>
                                        <Td className="font-bold text-amber-700">{c.coins_balance || 0}</Td>
                                        <Td className="text-xs">{inr(c.total_spent || 0)}</Td>
                                        <Td className="text-xs">{c.total_orders || 0}</Td>
                                        <Td className="text-right">
                                            <button
                                                onClick={() => setAdjust({ cust: c })}
                                                data-testid={`bal-adjust-${c.id}`}
                                                className="rounded-lg bg-primary text-primary-foreground px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1"
                                            ><WalletIcon size={11} /> Adjust</button>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : (
                <div className="bg-card rounded-2xl border border-border overflow-x-auto" data-testid="wd-table">
                    {busy ? (
                    <div className="py-16 text-center"><Loader2 className="animate-spin inline" /></div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground text-sm">No withdrawals.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-left">
                            <tr>
                                <Th>Customer</Th><Th>Amount</Th><Th>UPI</Th><Th>Requested</Th>
                                <Th>Status</Th><Th>UTR / Reason</Th><Th className="text-right">Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((w) => (
                                <tr key={w.id} className="border-t border-border" data-testid={`wd-row-${w.id}`}>
                                    <Td>
                                        <p className="font-semibold">{w.user_name || "—"}</p>
                                        <p className="text-[10px] text-muted-foreground font-mono">{w.user_mobile}</p>
                                    </Td>
                                    <Td>
                                        <span className="font-bold inline-flex items-center"><IndianRupee size={11} />{Math.round(w.amount).toLocaleString("en-IN")}</span>
                                    </Td>
                                    <Td>
                                        <span className="font-mono text-xs">{w.upi_id}</span>
                                    </Td>
                                    <Td>
                                        <span className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString()}</span>
                                    </Td>
                                    <Td>
                                        <Badge status={w.status} />
                                    </Td>
                                    <Td>
                                        {w.txn_ref && <span className="font-mono text-[11px] text-emerald-700">{w.txn_ref}</span>}
                                        {w.reject_reason && <span className="text-[11px] text-red-600">{w.reject_reason}</span>}
                                    </Td>
                                    <Td className="text-right">
                                        {w.status === "PENDING" ? (
                                            <div className="inline-flex gap-1">
                                                <button onClick={() => setAction({ wd: w, mode: "pay" })} className="rounded-lg bg-emerald-600 text-white px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1" data-testid={`wd-pay-${w.id}`}><CheckCircle2 size={12} /> Pay</button>
                                                <button onClick={() => setAction({ wd: w, mode: "reject" })} className="rounded-lg bg-red-600 text-white px-2.5 py-1 text-xs font-bold inline-flex items-center gap-1" data-testid={`wd-reject-${w.id}`}><XCircle size={12} /> Reject</button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">{new Date(w.processed_at || w.updated_at).toLocaleDateString()}</span>
                                        )}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                </div>
            )}

            {/* Pay/Reject modal */}
            {action && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !submitting && setAction(null)}>
                    <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="wd-action-modal">
                        <h2 className="text-lg font-bold mb-1">{action.mode === "pay" ? "Mark as Paid" : "Reject Withdrawal"}</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            {action.wd.user_name} · <b>{inr2(action.wd.amount)}</b> → <span className="font-mono">{action.wd.upi_id}</span>
                        </p>
                        {action.mode === "pay" ? (
                            <>
                                <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">UPI / UTR reference</label>
                                <input
                                    value={actionForm.txn_ref}
                                    onChange={(e) => setActionForm({ ...actionForm, txn_ref: e.target.value })}
                                    placeholder="UTR123456789"
                                    className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                                    data-testid="wd-action-txn"
                                />
                            </>
                        ) : (
                            <>
                                <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Reason (visible to customer)</label>
                                <textarea
                                    rows={3}
                                    value={actionForm.reason}
                                    onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
                                    placeholder="UPI ID invalid, please update and retry"
                                    className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                                    data-testid="wd-action-reason"
                                />
                                <p className="text-[10px] text-muted-foreground mt-2">The amount will be re-credited to the customer&apos;s wallet automatically.</p>
                            </>
                        )}
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setAction(null)} className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold" data-testid="wd-action-cancel">Cancel</button>
                            <button onClick={submitAction} disabled={submitting} className={`flex-1 rounded-xl ${action.mode === "pay" ? "bg-emerald-600" : "bg-red-600"} text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60`} data-testid="wd-action-confirm">{submitting && <Loader2 size={13} className="animate-spin" />}Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Adjust wallet modal */}
            {adjust && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !adjusting && setAdjust(null)}>
                    <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bal-adjust-modal">
                        <h2 className="text-lg font-bold mb-1">Adjust wallet</h2>
                        <p className="text-xs text-muted-foreground mb-4">
                            {`${adjust.cust.first_name || ""} ${adjust.cust.last_name || ""}`.trim() || adjust.cust.mobile_number} · current <b>{inr(adjust.cust.wallet_balance || 0)}</b>
                        </p>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Direction</label>
                        <div className="flex gap-2 mt-1 mb-3">
                            <button
                                onClick={() => setAdjustForm({ ...adjustForm, direction: "CREDIT" })}
                                data-testid="bal-adjust-credit"
                                className={`flex-1 rounded-xl py-2 text-sm font-bold inline-flex items-center justify-center gap-1 ${adjustForm.direction === "CREDIT" ? "bg-emerald-600 text-white" : "bg-muted text-foreground"}`}
                            ><Plus size={13} /> Credit</button>
                            <button
                                onClick={() => setAdjustForm({ ...adjustForm, direction: "DEBIT" })}
                                data-testid="bal-adjust-debit"
                                className={`flex-1 rounded-xl py-2 text-sm font-bold inline-flex items-center justify-center gap-1 ${adjustForm.direction === "DEBIT" ? "bg-red-600 text-white" : "bg-muted text-foreground"}`}
                            ><Minus size={13} /> Debit</button>
                        </div>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Amount (₹)</label>
                        <input
                            type="number" min="0" step="0.01"
                            value={adjustForm.amount}
                            onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })}
                            placeholder="100"
                            className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                            data-testid="bal-adjust-amount"
                        />
                        <label className="block mt-3 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Reason (mandatory · stored in audit log)</label>
                        <textarea
                            rows={2}
                            value={adjustForm.reason}
                            onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                            placeholder="Goodwill credit for complaint #1234"
                            className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none"
                            data-testid="bal-adjust-reason"
                        />
                        <label className="mt-3 flex items-center gap-2 text-xs">
                            <input
                                type="checkbox"
                                checked={adjustForm.notify_customer}
                                onChange={(e) => setAdjustForm({ ...adjustForm, notify_customer: e.target.checked })}
                                data-testid="bal-adjust-notify"
                                className="h-4 w-4"
                            />
                            Send push notification to the customer
                        </label>
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setAdjust(null)} className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold" data-testid="bal-adjust-cancel">Cancel</button>
                            <button
                                onClick={submitAdjust}
                                disabled={adjusting}
                                data-testid="bal-adjust-confirm"
                                className={`flex-1 rounded-xl ${adjustForm.direction === "CREDIT" ? "bg-emerald-600" : "bg-red-600"} text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60`}
                            >{adjusting && <Loader2 size={13} className="animate-spin" />}Confirm {adjustForm.direction === "CREDIT" ? "Credit" : "Debit"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

function Badge({ status }) {
    const cfg = STATUSES.find((s) => s.value === status) || { color: "bg-muted", label: status };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>;
}
